import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  DatePicker,
  InputNumber,
  Select,
} from 'components/FormFields';
import Modal from 'components/Modal';
import Form, { FormItem, useForm } from 'components/Form';
import { DATE_FIELD } from 'helper/const';
import { FREQUENCY_OPTIONS, MONTH_OPTIONS } from './constants';
import { actionCb } from 'utils/action';
import { getInitialValues, prepareFormData } from './utils';
import classes from './RecurringRuleModal.module.scss';

const RecurringRuleModal = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  editRule,
  orderTypes,
  createLoading,
  updateLoading,
  onCreateRule,
  onUpdateRule,
}) => {
  const [form] = useForm();
  const [frequency, setFrequency] = useState('monthly');
  const isEdit = !!editRule;
  const isLoading = createLoading || updateLoading;

  useEffect(() => {
    if (isOpen) {
      const initialValues = getInitialValues(editRule);
      form.resetFields();
      form.setFieldsValue(initialValues);
      setFrequency(initialValues.frequency || 'monthly');
    }
    // eslint-disable-next-line
  }, [isOpen, editRule]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then(values => {
        const payload = prepareFormData(values);
        if (isEdit) {
          onUpdateRule(
            companyId,
            editRule.id,
            payload,
            actionCb(() => {
              onSuccess();
              onClose();
            })
          );
        } else {
          onCreateRule(
            companyId,
            payload,
            actionCb(() => {
              onSuccess();
              onClose();
            })
          );
        }
      })
      .catch(() => {});
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={isEdit ? 'Edit Recurring Rule' : 'Create Recurring Rule'}
      okText={isEdit ? 'Save Changes' : 'Create Rule'}
      cancelText="Cancel"
      isSubmitLoading={isLoading}
      width={760}
    >
      <Form
        form={form}
        submitHidden
        requiredMark={false}
        onFinish={handleSubmit}
        className={classes.form}
      >
        <FormItem
          name="order_type_id"
          label="Order Type"
          required
          rules={[{ required: true, message: 'Order type is required.' }]}
        >
          <Select
            items={orderTypes || []}
            placeholder="Select order type"
            showSearch
            size="medium"
            name="order_type_id"
            form={form}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </FormItem>

        <FormItem
          name="frequency"
          label="Frequency"
          required
          rules={[{ required: true, message: 'Frequency is required.' }]}
        >
          <Select
            items={FREQUENCY_OPTIONS}
            placeholder="Select frequency"
            size="medium"
            name="frequency"
            form={form}
            onChange={val => {
              setFrequency(val);
              form.setFieldsValue({
                day_of_month: undefined,
                month_of_year: undefined,
              });
            }}
          />
        </FormItem>

        {(frequency === 'monthly' || frequency === 'quarterly') && (
          <FormItem
            name="day_of_month"
            label="Day of Month"
            required
            rules={[
              { required: true, message: 'Day of month is required.' },
              {
                type: 'number',
                min: 1,
                max: 31,
                message: 'Day of month must be between 1 and 31.',
              },
            ]}
          >
            <InputNumber
              name="day_of_month"
              form={form}
              min={1}
              max={31}
              precision={0}
              placeholder="Day (1–31)"
              size="middle"
              style={{ width: '100%' }}
            />
          </FormItem>
        )}

        {(frequency === 'yearly' || frequency === 'biennial') && (
          <FormItem
            name="month_of_year"
            label="Month of Year"
            required
            rules={[{ required: true, message: 'Month of year is required.' }]}
          >
            <Select
              items={MONTH_OPTIONS}
              placeholder="Select month"
              size="medium"
              name="month_of_year"
              form={form}
            />
          </FormItem>
        )}

        <FormItem
          name="start_date"
          label="Start Date"
          required
          rules={[{ required: true, message: 'Start date is required.' }]}
        >
          <DatePicker
            name="start_date"
            form={form}
            format={DATE_FIELD.FORMAT}
            size="medium"
            placeholder="MM/DD/YYYY"
            style={{ width: '100%' }}
          />
        </FormItem>

        <FormItem name="end_date" label="End Date (optional)">
          <DatePicker
            name="end_date"
            form={form}
            format={DATE_FIELD.FORMAT}
            size="medium"
            placeholder="MM/DD/YYYY"
            style={{ width: '100%' }}
          />
        </FormItem>

        <FormItem
          name="auto_submit"
          valuePropName="checked"
          className={classes.fullWidthItem}
        >
          <Checkbox name="auto_submit" form={form} label="Auto Submit" />
        </FormItem>

        {isEdit && (
          <FormItem
            name="active"
            valuePropName="checked"
            className={classes.fullWidthItem}
          >
            <Checkbox name="active" form={form} label="Active" />
          </FormItem>
        )}
      </Form>
    </Modal>
  );
};

export default RecurringRuleModal;
